import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { tierDisplayName } from '../utils/tierNames';
import NotificationBell from './NotificationBell';
import {
  Zap, LayoutDashboard, Image as ImageIcon, Video, Music,
  Users, FolderOpen, Sparkles, Calendar, MessageCircle, MessageSquare, LayoutGrid,
  Gift, CreditCard, Settings as SettingsIcon, LogOut, Menu, X, Shield,
  Target, BarChart3, Coins, Activity, Layers, Palette, Wand2, Film, Eye, Sun, BrainCircuit, Network, Rocket, GraduationCap, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAV_SECTIONS = [
  {
    items: [
      { icon: LayoutDashboard, label: 'Home', path: '/dashboard' },
      { icon: MessageCircle, label: 'Ask Delphi', path: '/delphi' },
      { icon: Sparkles, label: 'Autonomous Delphi', path: '/autonomous' },
      { icon: FolderOpen, label: 'Library', path: '/library' },
      { icon: Calendar, label: 'Schedule', path: '/scheduler' },
      { icon: MessageSquare, label: 'Social Inbox', path: '/engagement' },
      { icon: BarChart3, label: 'Analytics', path: '/campaign-analytics' },
    ]
  },
  {
    label: 'CREATE',
    advanced: true,
    items: [
      { icon: ImageIcon, label: 'Create Image', path: '/studio/images' },
      { icon: Video, label: 'Create Video', path: '/studio/videos' },
      { icon: Film, label: 'Cinema', path: '/cinema' },
      { icon: Music, label: 'Create Music', path: '/studio/music' },
      { icon: Layers, label: 'Combine', path: '/studio/combine' },
      { icon: Sparkles, label: 'Templates', path: '/templates' },
      { icon: LayoutGrid, label: 'Canvas', path: '/canvas' },
    ]
  },
  {
    label: 'ADVANCED',
    advanced: true,
    items: [
      { icon: Users, label: 'Characters', path: '/influencers' },
      { icon: Palette, label: 'Apps', path: '/apps' },
      { icon: Target, label: 'Marketing Studio', path: '/campaigns' },
      { icon: Wand2, label: 'Production Engine', path: '/create' },
      { icon: Eye, label: 'Delphi Classic', path: '/oracle' },
      { icon: BrainCircuit, label: 'Autopilot', path: '/autonomy' },
      { icon: Network, label: 'Delphi at Work', path: '/workforce' },
      { icon: Rocket, label: 'Growth Command', path: '/growth-command', founderOnly: true },
      { icon: GraduationCap, label: 'Performance Learning', path: '/learning' },
      { icon: Sun, label: 'Relight Studio', path: '/relight' },
    ]
  },
  {
    label: 'ACCOUNT',
    items: [
      { icon: Gift, label: 'Referrals', path: '/affiliates' },
      { icon: CreditCard, label: 'Billing', path: '/billing' },
      { icon: SettingsIcon, label: 'Settings', path: '/settings' },
      { icon: Activity, label: 'Diagnostics', path: '/diagnostics', founderOnly: true },
    ]
  },
];

export default function AppShell({ children }) {
  const { user, token, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  const advancedPaths = NAV_SECTIONS.filter(s => s.advanced).flatMap(s => s.items.map(i => i.path));
  const onAdvancedRoute = advancedPaths.some(p => location.pathname === p || (p !== '/create' && location.pathname.startsWith(p)));
  const [advancedOpen, setAdvancedOpen] = useState(onAdvancedRoute);

  useEffect(() => {
    if (!token) return;
    const fetchCredits = async () => {
      try {
        const resp = await axios.get(`${API}/credits/balance`, { headers: { Authorization: `Bearer ${token}` } });
        setCreditBalance(resp.data);
      } catch {}
    };
    fetchCredits();
    const interval = setInterval(fetchCredits, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Session ended');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="app-shell">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`} data-testid="app-sidebar">
        {/* Brand */}
        <div className="px-5 h-14 flex items-center border-b border-border/40">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
              <Zap className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-[14px] font-bold tracking-tight text-zinc-100" style={{ fontFamily: 'Outfit, sans-serif' }}>Delphi AI</span>
          </Link>
        </div>

        {/* User */}
        <div className="px-4 py-3 border-b border-border/40">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-zinc-700/80 flex items-center justify-center text-xs font-semibold text-zinc-300">
              {user?.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-zinc-200 truncate">{user?.name || 'User'}</p>
              <p className="text-[10px] text-zinc-500 uppercase tracking-[0.1em] font-medium">
                {tierDisplayName(user?.tier)}{user?.is_founder ? ' · Founder' : ''}
              </p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-2 px-2.5 overflow-y-auto" data-testid="app-nav">
          {NAV_SECTIONS.map((section, si) => {
            if (section.advanced && section.label !== 'CREATE' && !advancedOpen) return null;
            if (section.advanced && section.label === 'CREATE' && !advancedOpen) {
              return (
                <div key={si} className="mt-4 border-t border-border/40 pt-3">
                  <button
                    onClick={() => setAdvancedOpen(true)}
                    data-testid="nav-advanced-tools-toggle"
                    className="w-full flex items-center gap-2 px-3 py-[7px] rounded-lg text-[12px] font-medium text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700/40 transition-colors duration-150"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                    <span>Advanced tools</span>
                  </button>
                </div>
              );
            }
            return (
            <div key={si} className={si > 0 ? 'mt-4' : ''}>
              {section.advanced && section.label === 'CREATE' && (
                <button
                  onClick={() => setAdvancedOpen(false)}
                  data-testid="nav-advanced-tools-toggle"
                  className="w-full flex items-center gap-2 px-3 mb-2 py-[7px] rounded-lg text-[12px] font-medium text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700/40 transition-colors duration-150 border-t border-border/40"
                >
                  <ChevronRight className="w-3.5 h-3.5 rotate-90" />
                  <span>Advanced tools</span>
                </button>
              )}
              {section.label && (
                <p className="px-3 mb-1 text-[9px] text-zinc-500 uppercase tracking-[0.15em] font-semibold">
                  {section.label}
                </p>
              )}
              <div className="space-y-0.5">
                {section.items
                  .filter(item => !item.founderOnly || user?.is_founder)
                  .map((item) => {
                  const isActive = location.pathname === item.path ||
                    (item.path !== '/dashboard' && item.path !== '/create' && location.pathname.startsWith(item.path));
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                      className={`flex items-center gap-2.5 px-3 py-[7px] rounded-lg text-[13px] font-medium transition-all duration-150 ${
                        isActive
                          ? 'bg-orange-500/[0.08] text-orange-400'
                          : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700/40'
                      }`}
                    >
                      <item.icon className="w-[16px] h-[16px]" />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
            );
          })}

          {/* Admin link for founders */}
          {user?.is_founder && (
            <div className="mt-4">
              <p className="px-3 mb-1 text-[9px] text-zinc-500 uppercase tracking-[0.15em] font-semibold">ADMIN</p>
              <Link
                to="/admin"
                onClick={() => setSidebarOpen(false)}
                data-testid="nav-admin-console"
                className={`flex items-center gap-2.5 px-3 py-[7px] rounded-lg text-[13px] font-medium transition-all duration-150 ${
                  location.pathname === '/admin'
                    ? 'bg-orange-500/[0.08] text-orange-400'
                    : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700/40'
                }`}
              >
                <Shield className="w-[16px] h-[16px]" />
                <span>Admin Console</span>
              </Link>
            </div>
          )}
        </nav>

        {/* Credit Balance */}
        {creditBalance && (
          <Link
            to="/billing"
            className="mx-2.5 mb-2 px-3 py-2 rounded-lg bg-zinc-800/60 border border-zinc-700/50 hover:border-orange-500/30 transition-all group"
            data-testid="credit-balance-indicator"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Coins className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-[11px] text-zinc-400 group-hover:text-zinc-300">Credits</span>
              </div>
              <span className="text-[13px] font-bold text-zinc-100" data-testid="credit-balance-value">
                {(creditBalance.balance || 0).toLocaleString()}
              </span>
            </div>
            <div className="mt-1.5 h-1 bg-zinc-700 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-orange-500 to-red-600 transition-all duration-500"
                style={{ width: `${Math.min(100, creditBalance.monthly_credits > 0 ? ((creditBalance.monthly_remaining || 0) / creditBalance.monthly_credits) * 100 : 100)}%` }}
              />
            </div>
            <p className="text-[9px] text-zinc-600 mt-1">{creditBalance.monthly_remaining || 0} monthly remaining</p>
          </Link>
        )}

        {/* Support links */}
        <div className="px-2.5 pt-2 pb-1">
          <div className="flex items-center gap-3 px-3 text-[10px] text-zinc-500">
            <Link to="/help" className="hover:text-zinc-300 transition-colors" data-testid="sidebar-help-link">Help</Link>
            <span className="text-zinc-700">·</span>
            <Link to="/contact" className="hover:text-zinc-300 transition-colors" data-testid="sidebar-contact-link">Contact</Link>
            <span className="text-zinc-700">·</span>
            <Link to="/status" className="hover:text-zinc-300 transition-colors" data-testid="sidebar-status-link">Status</Link>
          </div>
        </div>

        {/* Logout */}
        <div className="px-2.5 py-2.5 border-t border-border/40">
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center gap-2.5 px-3 py-[7px] rounded-lg w-full text-[13px] font-medium text-zinc-500 hover:text-red-400 hover:bg-red-500/[0.05] transition-all duration-150"
          >
            <LogOut className="w-[16px] h-[16px]" />
            <span>Log Out</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 h-14 flex items-center justify-between px-4 bg-background/90 backdrop-blur-xl border-b border-border/40" data-testid="mobile-header">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-btn"
          className="p-2 rounded-lg hover:bg-zinc-700/40 transition-all duration-150"
        >
          {sidebarOpen ? <X className="w-5 h-5 text-zinc-400" /> : <Menu className="w-5 h-5 text-zinc-400" />}
        </button>
        <Link to="/" className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-orange-500" />
          <span className="text-sm font-bold text-zinc-100" style={{ fontFamily: 'Outfit, sans-serif' }}>Delphi AI</span>
        </Link>
        <NotificationBell />
      </header>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-30"
          onClick={() => setSidebarOpen(false)}
          data-testid="mobile-overlay"
        />
      )}

      {/* Main content */}
      <main className="main-with-sidebar pt-14 lg:pt-0 min-h-screen">
        {children}
      </main>
    </div>
  );
}
